import { useState } from 'react'

import { PageLayout } from '@components/layouts/PageLayout'

import { DesignExplorer, SegControl } from '../components/DesignExplorer'
import { ImportSummary, type Viewer } from '../components/imports/ImportSummary'
import { CLEAN_RUN, type ImportRun, SETTLED_RUN, TYPE_LABELS, WORKING_RUN } from '../components/imports/sampleData'

/**
 * Import summary (design-phase prototype, story 02 / P0.1)
 *
 * The standalone page at `/imports/:id` (P3.3), and at
 * `/groups/:id/imports/:import_id` for captains. The same summary component
 * step 3 of SupporterImportPreview renders, with the admin-only file download
 * and the "file removed" variant that only make sense on the standalone page.
 */

type RunState = 'working' | 'settled' | 'clean'

const RUNS: Record<RunState, ImportRun> = {
  working: WORKING_RUN,
  settled: SETTLED_RUN,
  clean: CLEAN_RUN,
}

const ImportSummaryPreview = () => {
  const [runState, setRunState] = useState<RunState>('settled')
  const [viewer, setViewer] = useState<Viewer | 'other_captain'>('admin')
  const [fileAvailable, setFileAvailable] = useState(true)
  const [reviewExtension, setReviewExtension] = useState(false)
  const [reviewed, setReviewed] = useState(false)

  const run = RUNS[runState]
  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="upload-cloud-02"
        heading={`Import #${run.id}`}
        subtitle={
          <>
            {TYPE_LABELS[run.type]} into <span className="tw:font-medium tw:text-gray-cool-900">{run.group}</span> ·{' '}
            {run.submittedAt}
          </>
        }
      />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:max-w-5xl">
          <ImportSummary
            run={run}
            viewer={viewer === 'admin' ? 'admin' : 'captain'}
            fileAvailable={fileAvailable}
            canDownloadRows={viewer !== 'other_captain'}
            review={reviewExtension ? { reviewed, onChange: setReviewed } : undefined}
          />
        </div>
      </PageLayout.Body>

      <DesignExplorer>
        <SegControl
          label="Run state"
          value={runState}
          onChange={(value) => setRunState(value)}
          options={[
            { value: 'working', label: 'Working' },
            { value: 'settled', label: 'Settled' },
            { value: 'clean', label: 'No failures' },
          ]}
        />
        <SegControl
          label="Viewer"
          value={viewer}
          onChange={(value) => setViewer(value)}
          options={[
            { value: 'admin', label: 'Admin' },
            { value: 'captain', label: 'Captain who ran it' },
            { value: 'other_captain', label: 'Another captain' },
          ]}
        />
        <SegControl
          label="Uploaded file"
          value={fileAvailable ? 'kept' : 'removed'}
          onChange={(value) => setFileAvailable(value === 'kept')}
          options={[
            { value: 'kept', label: 'Still stored' },
            { value: 'removed', label: 'Removed after 90 days' },
          ]}
        />
        <SegControl
          label="Mark as reviewed (optional extension)"
          value={reviewExtension ? 'on' : 'off'}
          onChange={(value) => setReviewExtension(value === 'on')}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'on', label: 'On' },
          ]}
        />
      </DesignExplorer>
    </PageLayout.Root>
  )
}

export default ImportSummaryPreview
